/**
 * Histogram Renderer
 * 
 * Renders histogram with SVG-based bar chart.
 * Auto-scales bins and supports tooltips on hover.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderHistogram: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { bin: string, count: number } or { value: number, frequency: number }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const bins = data.map(item => {
    // Support both { bin, count } and { value, frequency } formats
    const bin = item.bin !== undefined ? String(item.bin) : (item.value !== undefined ? String(item.value) : '');
    const count = item.count !== undefined ? Number(item.count) : (item.frequency !== undefined ? Number(item.frequency) : 0);
    return { bin, count };
  });

  // Calculate dimensions
  const width = 600;
  const height = 300;
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find max count for scaling
  const maxCount = Math.max(...bins.map(b => b.count), 1);

  // Calculate bar width
  const barWidth = chartWidth / bins.length;
  const barSpacing = barWidth * 0.1; // 10% spacing between bars
  const actualBarWidth = barWidth - barSpacing;

  // Generate bars
  const bars = bins.map((item, index) => {
    const barHeight = (item.count / maxCount) * chartHeight;
    const x = padding.left + (index * barWidth) + (barSpacing / 2);
    const y = padding.top + chartHeight - barHeight;
    
    return {
      ...item,
      x,
      y,
      width: actualBarWidth,
      height: barHeight
    };
  });

  // Generate bar elements with tooltips
  const barElements = bars.map((bar, index) => {
    const tooltipId = `tooltip-${Date.now()}-${index}`;
    
    return `
      <g>
        <rect
          x="${bar.x}"
          y="${bar.y}"
          width="${bar.width}"
          height="${bar.height}"
          fill="rgba(102, 204, 255, 0.6)"
          stroke="rgba(102, 204, 255, 0.8)"
          stroke-width="1"
          style="cursor: pointer; transition: fill 0.2s;"
          data-bin="${escapeHtml(bar.bin)}"
          data-count="${bar.count}"
          onmouseenter="
            this.style.fill = 'rgba(102, 204, 255, 0.9)';
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'block';
          "
          onmouseleave="
            this.style.fill = 'rgba(102, 204, 255, 0.6)';
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'none';
          "
        />
        <text
          x="${bar.x + bar.width / 2}"
          y="${height - padding.bottom + 15}"
          text-anchor="middle"
          font-size="${designTokens.fontSize.xs}"
          fill="${designTokens.contentColorMuted}"
          transform="rotate(-45 ${bar.x + bar.width / 2} ${height - padding.bottom + 15})"
        >${escapeHtml(bar.bin.length > 10 ? bar.bin.substring(0, 10) + '...' : bar.bin)}</text>
        <g id="${tooltipId}" style="display: none; pointer-events: none;">
          <rect
            x="${bar.x + bar.width / 2 - 40}"
            y="${bar.y - 30}"
            width="80"
            height="25"
            fill="rgba(30, 32, 40, 0.95)"
            stroke="${designTokens.borderColor}"
            stroke-width="1"
            rx="4"
          />
          <text
            x="${bar.x + bar.width / 2}"
            y="${bar.y - 12}"
            text-anchor="middle"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColor}"
          >${bar.bin}: ${bar.count}</text>
        </g>
      </g>
    `;
  }).join('');

  // Generate Y-axis labels
  const yAxisSteps = 5;
  const yAxisLabels = Array.from({ length: yAxisSteps + 1 }, (_, i) => {
    const value = (maxCount / yAxisSteps) * (yAxisSteps - i);
    const y = padding.top + (chartHeight / yAxisSteps) * i;
    
    return `
      <text
        x="${padding.left - 10}"
        y="${y + 4}"
        text-anchor="end"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColorMuted}"
      >${Math.round(value)}</text>
      <line
        x1="${padding.left}"
        y1="${y}"
        x2="${padding.left + chartWidth}"
        y2="${y}"
        stroke="${designTokens.borderColor}"
        stroke-width="0.5"
        opacity="0.3"
      />
    `;
  }).join('');

  // Generate axis lines
  const axisLines = `
    <line
      x1="${padding.left}"
      y1="${padding.top}"
      x2="${padding.left}"
      y2="${padding.top + chartHeight}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
    />
    <line
      x1="${padding.left}"
      y1="${padding.top + chartHeight}"
      x2="${padding.left + chartWidth}"
      y2="${padding.top + chartHeight}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
    />
  `;

  const title = meta?.title || 'Histogram';
  const units = meta?.units || '';

  return `
    <div style="width: 100%;">
      ${title ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">${escapeHtml(title)}</div>
      ` : ''}
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${width}"
          height="${height}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${axisLines}
          ${yAxisLabels}
          ${barElements}
        </svg>
      </div>
      ${units ? `
        <div style="
          margin-top: ${designTokens.padding.sm};
          font-size: ${designTokens.fontSize.xs};
          color: ${designTokens.contentColorMuted};
          text-align: center;
        ">Units: ${escapeHtml(units)}</div>
      ` : ''}
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


